import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Users, Download, Mail, Phone, Calendar, FileText, Search, Loader2, 
  RefreshCw, Briefcase, Trash2, Video, AlertTriangle, CheckCircle2,
  BookOpen, ClipboardList
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { user, loading: authLoading, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { state: { from: "/admin" } });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    fetchLeads();
  }, [user, getAuthHeader]);

  const fetchLeads = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/leads`, {
        headers: getAuthHeader()
      });
      setLeads(response.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    
    setDeleting(leadId);
    try {
      await axios.delete(`${API_URL}/api/leads/${leadId}`, {
        headers: getAuthHeader()
      });
      setLeads(leads.filter(l => l.id !== leadId));
      toast.success("Lead deleted successfully");
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Failed to delete lead");
    } finally {
      setDeleting(null);
    }
  };

  const exportToCSV = (dataToExport, filename) => {
    const headers = ["Name", "Email", "Phone", "Type", "Training/Service", "Message", "Date"];
    const csvContent = [
      headers.join(","),
      ...dataToExport.map(lead => [
        `"${lead.name || ''}"`,
        `"${lead.email || ''}"`,
        `"${lead.phone || ''}"`,
        `"${getLeadType(lead)}"`,
        `"${lead.training || lead.service_interest || ''}"`,
        `"${(lead.message || '').replace(/"/g, '""')}"`,
        `"${new Date(lead.created_at).toLocaleDateString()}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success("Exported successfully!");
  };

  const getLeadType = (lead) => {
    if (lead.type === "training_registration") return "Training Registration";
    if (lead.service_interest === "free-resource") return "Free Resource";
    if (lead.service_interest) return "Service Inquiry";
    return "Contact Form";
  };

  const getLeadIcon = (lead) => {
    if (lead.type === "training_registration") return Video;
    if (lead.service_interest === "free-resource") return FileText;
    if (lead.service_interest) return Briefcase;
    return Mail;
  };

  // Filter leads based on active tab
  const trainingLeads = leads.filter(l => l.type === "training_registration");
  const otherLeads = leads.filter(l => l.type !== "training_registration");

  const getFilteredLeads = () => {
    let filtered = activeTab === "training" ? trainingLeads : 
                   activeTab === "all" ? leads : otherLeads;
    
    if (searchTerm) {
      filtered = filtered.filter(lead =>
        lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  const filteredLeads = getFilteredLeads();

  const stats = {
    total: leads.length,
    training: trainingLeads.length,
    freeResource: leads.filter(l => l.service_interest === "free-resource").length,
    serviceInquiry: leads.filter(l => l.service_interest && l.service_interest !== "free-resource" && l.type !== "training_registration").length,
    contact: leads.filter(l => !l.service_interest && l.type !== "training_registration").length
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1E3A5F] py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Admin Dashboard
              </h1>
              <p className="text-gray-300">Manage leads and training registrations</p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={fetchLeads}
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button 
                onClick={() => exportToCSV(filteredLeads, activeTab === "training" ? "training-registrations" : "omw-leads")}
                className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white"
              >
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Quick Access Tools */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
          <h2 className="text-lg font-bold text-[#1E3A5F] mb-4">Quick Access</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/workbook')}
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-[#1E3A5F]/5 transition-colors text-left"
            >
              <div className="h-10 w-10 rounded-lg bg-[#9D9045]/10 flex items-center justify-center">
                <ClipboardList className="h-5 w-5 text-[#9D9045]" />
              </div>
              <div>
                <p className="font-semibold text-[#1E3A5F]">Financial Clarity Workbook</p>
                <p className="text-xs text-gray-500">Interactive workbook</p>
              </div>
            </button>
            <button
              onClick={() => window.open('/financial-clarity-workbook.md', '_blank')}
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-[#1E3A5F]/5 transition-colors text-left"
            >
              <div className="h-10 w-10 rounded-lg bg-[#1E3A5F]/10 flex items-center justify-center">
                <Download className="h-5 w-5 text-[#1E3A5F]" />
              </div>
              <div>
                <p className="font-semibold text-[#1E3A5F]">Download Workbook PDF</p>
                <p className="text-xs text-gray-500">Printable version</p>
              </div>
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-[#1E3A5F]/5 transition-colors text-left"
            >
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-[#1E3A5F]">My Ebooks (6)</p>
                <p className="text-xs text-gray-500">View all ebooks</p>
              </div>
            </button>
            <button
              onClick={() => navigate('/training')}
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-[#1E3A5F]/5 transition-colors text-left"
            >
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Video className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-[#1E3A5F]">Free Training Page</p>
                <p className="text-xs text-gray-500">View training funnel</p>
              </div>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#1E3A5F]/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-[#1E3A5F]" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#1E3A5F]">{stats.total}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border-2 border-[#9D9045]">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#9D9045]/10 flex items-center justify-center">
                <Video className="h-5 w-5 text-[#9D9045]" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#9D9045]">{stats.training}</p>
                <p className="text-xs text-gray-500">Training</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#1E3A5F]">{stats.freeResource}</p>
                <p className="text-xs text-gray-500">Downloads</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#1E3A5F]">{stats.serviceInquiry}</p>
                <p className="text-xs text-gray-500">Services</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#1E3A5F]">{stats.contact}</p>
                <p className="text-xs text-gray-500">Contact</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="border-b">
            <div className="flex">
              <button
                onClick={() => setActiveTab("training")}
                className={`flex items-center gap-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === "training"
                    ? "border-[#9D9045] text-[#9D9045]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Video className="h-4 w-4" />
                Training Registrations
                <span className="bg-[#9D9045] text-white text-xs px-2 py-0.5 rounded-full">{stats.training}</span>
              </button>
              <button
                onClick={() => setActiveTab("all")}
                className={`flex items-center gap-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === "all"
                    ? "border-[#1E3A5F] text-[#1E3A5F]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Users className="h-4 w-4" />
                All Leads
                <span className="bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full">{stats.total}</span>
              </button>
              <button
                onClick={() => setActiveTab("other")}
                className={`flex items-center gap-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === "other"
                    ? "border-[#1E3A5F] text-[#1E3A5F]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Mail className="h-4 w-4" />
                Other Inquiries
                <span className="bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full">{otherLeads.length}</span>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="p-4 border-b">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-4 px-6 font-medium text-gray-500 text-sm">Contact</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-500 text-sm">Type</th>
                  {activeTab === "training" ? (
                    <th className="text-left py-4 px-6 font-medium text-gray-500 text-sm">Training</th>
                  ) : (
                    <th className="text-left py-4 px-6 font-medium text-gray-500 text-sm">Details</th>
                  )}
                  <th className="text-left py-4 px-6 font-medium text-gray-500 text-sm">Date</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-500 text-sm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        {activeTab === "training" ? (
                          <>
                            <Video className="h-8 w-8 text-gray-300" />
                            <p>No training registrations yet</p>
                          </>
                        ) : (
                          <>
                            <Users className="h-8 w-8 text-gray-300" />
                            <p>No leads match your search</p>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead) => {
                    const LeadIcon = getLeadIcon(lead);
                    return (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center text-[#1E3A5F] font-medium">
                              {lead.name?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{lead.name}</p>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {lead.email}
                                </span>
                                {lead.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {lead.phone}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                            lead.type === "training_registration"
                              ? "bg-[#9D9045]/10 text-[#9D9045]"
                              : lead.service_interest === "free-resource" 
                                ? "bg-green-100 text-green-700"
                                : lead.service_interest 
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-blue-100 text-blue-700"
                          }`}>
                            <LeadIcon className="h-3 w-3" />
                            {getLeadType(lead)}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          {activeTab === "training" || lead.type === "training_registration" ? (
                            <span className="text-sm text-gray-600">
                              {lead.training === "time-to-act-debt" 
                                ? "Time to Act on Debt" 
                                : lead.training || "General Training"}
                            </span>
                          ) : (
                            <p className="text-gray-600 text-sm max-w-xs truncate">
                              {lead.service_interest || lead.message || "-"}
                            </p>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Calendar className="h-3 w-3" />
                            {new Date(lead.created_at).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-400">
                            {new Date(lead.created_at).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteLead(lead.id)}
                            disabled={deleting === lead.id}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            {deleting === lead.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results Count */}
        <div className="text-sm text-gray-500 text-center">
          Showing {filteredLeads.length} of {activeTab === "training" ? stats.training : activeTab === "other" ? otherLeads.length : leads.length} leads
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
